import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { Layers, Ruler, Upload, Map as MapIcon, Satellite, Sun, Locate, Loader2, Eye, EyeOff, MapPin, Router, HardHat, Magnet, Unlink, Trash2, Users, Zap, Milestone, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";
import { resolveBaseLayers } from "@/lib/mapTiles";
import { MODULE_ORDER, MODULES } from "@/lib/modules";
import MapView from "@/components/MapView";
import Sidebar from "@/components/Sidebar";
import SipDialer from "@/components/SipDialer";
import SmsInboxModal from "@/components/SmsInboxModal";
import ModulePanel from "@/components/ModulePanel";
import ObjectDetailPanel from "@/components/ObjectDetailPanel";
import RecordForm from "@/components/RecordForm";
import FibersPanel from "@/components/FibersPanel";
import TracePanel from "@/components/TracePanel";
import FiberTracePanel from "@/components/FiberTracePanel";
import ReportsPanel from "@/components/ReportsPanel";
import AIPanel from "@/components/AIPanel";
import ConnectionsPanel from "@/components/ConnectionsPanel";
import ReservationsPanel from "@/components/ReservationsPanel";
import FaultsPanel from "@/components/FaultsPanel";
import SNMPPanel from "@/components/SNMPPanel";
import PingPanel from "@/components/PingPanel";
import SchedulePanel from "@/components/SchedulePanel";
import LogsPanel from "@/components/LogsPanel";
import QRLabelsPanel from "@/components/QRLabelsPanel";
import OtdrExplorer from "@/components/OtdrExplorer";
import UserManagementPanel from "@/components/UserManagementPanel";
import SettingsPanel from "@/components/SettingsPanel";
import SecurityPanel from "@/components/SecurityPanel";
import RequestsPanel from "@/components/RequestsPanel";
import HelpPanel from "@/components/HelpPanel";
import GuidedTour from "@/components/GuidedTour";
import { TOURS } from "@/lib/tours";
import StatsDrawer from "@/components/StatsDrawer";
import ChatDrawer from "@/components/ChatDrawer";
import BuildBadge from "@/components/BuildBadge";
import TrayView from "@/components/TrayView";
import SplitterView from "@/components/SplitterView";
import { ClientRoutePrompt, SplitterConnectDialog } from "@/components/ClientRouteDialogs";
import ODFView from "@/components/ODFView";
import OLTView from "@/components/OLTView";
import SwitchView from "@/components/SwitchView";
import StatsPanel from "@/components/StatsPanel";
import ClosureView from "@/components/ClosureView";
import InvoicesPanel from "@/components/InvoicesPanel";
import WarehousePanel from "@/components/WarehousePanel";
import RoutersPanel from "@/components/RoutersPanel";
import SwitchMgmtPanel from "@/components/SwitchMgmtPanel";
import WifiPanel from "@/components/wifi/WifiPanel";
import DashboardPanel from "@/components/DashboardPanel";
import Client360Panel from "@/components/Client360Panel";
import ClientImportDialog from "@/components/ClientImportDialog";
import PitUkePanel from "@/components/PitUkePanel";
import SidusisPanel from "@/components/SidusisPanel";
import AutoPlanPanel from "@/components/AutoPlanPanel";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import CabinetView from "@/components/CabinetView";
import FloatingPanel from "@/components/FloatingPanel";
import { loadShortcuts, normalizeCombo } from "@/lib/shortcuts";
import SchematicView from "@/components/SchematicView";
import ImportDialog from "@/components/ImportDialog";
import ExportDialog from "@/components/ExportDialog";
import SearchBar from "@/components/SearchBar";
import CableSlackDialog from "@/components/CableSlackDialog";
import SlackProposalDialog from "@/components/SlackProposalDialog";

export default function Workspace() {
  const { user, logout, isMaster } = useAuth();
  const { settings } = useSettings();
  const { t } = useI18n();
  const tiles = resolveBaseLayers(settings);
  const [data, setData] = useState({});
  const [layers, setLayers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [hiddenFolders, setHiddenFolders] = useState(() => { try { return JSON.parse(localStorage.getItem("fp_hidden_folders") || "[]"); } catch { return []; } });
  const loadFolders = useCallback(() => api.get("/folders").then((r) => setFolders(r.data || [])).catch(() => {}), []);
  const [deviceLinks, setDeviceLinks] = useState([]);
  const [showLinks, setShowLinks] = useState(false);
  const [active, setActive] = useState(null);
  const [client360Init, setClient360Init] = useState(null);
  const [base, setBase] = useState("osm");
  const [flyTarget, setFlyTarget] = useState(null);
  useEffect(() => {
    const onFly = (e) => { if (e.detail?.lat != null) setFlyTarget({ lat: Number(e.detail.lat), lng: Number(e.detail.lng) }); };
    const onOpenClient = (e) => { if (e.detail?.clientId) { setActive(null); setClient360Init(e.detail.clientId); setPanel("client360"); } };
    window.addEventListener("fp-flyto", onFly);
    window.addEventListener("fp-open-client360", onOpenClient);
    return () => { window.removeEventListener("fp-flyto", onFly); window.removeEventListener("fp-open-client360", onOpenClient); };
  }, []);

  const [addMode, setAddMode] = useState(null); // moduleKey
  const [pendingCoords, setPendingCoords] = useState(null);
  const [measureMode, setMeasureMode] = useState(false);
  const [measurePts, setMeasurePts] = useState([]);
  const [boxMode, setBoxMode] = useState(false);
  const [boxPts, setBoxPts] = useState([]);
  const [boxCount, setBoxCount] = useState(null);
  const [boxBusy, setBoxBusy] = useState(false);
  const boxBounds = () => ({
    min_lat: Math.min(boxPts[0][0], boxPts[1][0]), max_lat: Math.max(boxPts[0][0], boxPts[1][0]),
    min_lng: Math.min(boxPts[0][1], boxPts[1][1]), max_lng: Math.max(boxPts[0][1], boxPts[1][1]),
  });
  const boxPreview = async () => {
    setBoxBusy(true);
    try { const r = await api.post("/coverage/bounds-delete-preview", boxBounds()); setBoxCount(r.data.total); }
    catch { setBoxCount(0); } finally { setBoxBusy(false); }
  };
  const boxDelete = async () => {
    if (!window.confirm(`Usunąć zasięg z zaznaczonego obszaru (${boxCount})? Tej operacji nie można cofnąć (dostępny Kosz).`)) return;
    setBoxBusy(true);
    try {
      const r = await api.post("/coverage/bounds-delete", boxBounds());
      toast.success(`Usunięto z zasięgu: ${r.data.deleted}`);
      setBoxMode(false); setBoxPts([]); setBoxCount(null); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Błąd usuwania"); } finally { setBoxBusy(false); }
  };
  const boxHighlight = useMemo(() => {
    if (!boxMode || boxPts.length !== 2) return [];
    const a = Math.min(boxPts[0][0], boxPts[1][0]), b = Math.max(boxPts[0][0], boxPts[1][0]);
    const c = Math.min(boxPts[0][1], boxPts[1][1]), d = Math.max(boxPts[0][1], boxPts[1][1]);
    return ((data && data.coverage) || [])
      .filter((x) => x && x.lat != null && x.lng != null && x.lat >= a && x.lat <= b && x.lng >= c && x.lng <= d)
      .map((x) => [x.lat, x.lng]);
  }, [boxMode, boxPts, data]);

  const [drawMode, setDrawMode] = useState(null); // moduleKey for line modules
  const [drawPoints, setDrawPoints] = useState([]);
  const [extendCtx, setExtendCtx] = useState(null); // { cableId, end } — przedłużanie trasy kabla
  const [snapOff, setSnapOff] = useState(false);
  const [drawGeometry, setDrawGeometry] = useState(null); // {module, geometry, length}
  const [blinkSplitterId, setBlinkSplitterId] = useState(null);
  const [clientCableDraw, setClientCableDraw] = useState(false);
  const [clientCableGeo, setClientCableGeo] = useState(null);
  const [clientRoutePrompt, setClientRoutePrompt] = useState(null); // saved client awaiting route decision
  const [clientRoute, setClientRoute] = useState(null); // { clientId, clientName, house } active auto-route draw
  const [declinedSplitters, setDeclinedSplitters] = useState([]); // splitter ids skipped during draw
  const [splitterConnect, setSplitterConnect] = useState(null); // { splitter, closure, pos } port picker

  const [tracePath, setTracePath] = useState(null);
  const [trace, setTrace] = useState(null);
  const [fiberTrace, setFiberTrace] = useState(null);
  const [fibersCable, setFibersCable] = useState(null);
  const [detail, setDetail] = useState(null); // {module, item}
  const [spotlight, setSpotlight] = useState(null); // {module, id, isolate}
  const [splitterPower, setSplitterPower] = useState({});
  const [showSplitterPower, setShowSplitterPower] = useState(false);
  const loadLayers = () => { try { return JSON.parse(localStorage.getItem("fp_map_layers") || "{}") || {}; } catch { return {}; } };
  const [rxLayer, setRxLayer] = useState(() => !!loadLayers().rx);
  const [egibLayer, setEgibLayer] = useState(() => !!loadLayers().egib);
  const [gesut, setGesut] = useState(() => loadLayers().gesut || {});
  const [mapKey, setMapKey] = useState(0);
  const layersLoadedRef = useRef(false);
  const layersSaveTimer = useRef(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const L = r.data?.map_layers;
      if (L) {
        setRxLayer(!!L.rx); setEgibLayer(!!L.egib); setGesut(L.gesut || {});
        if (L.base) setBase(L.base);
        if (L.view && L.view.lat != null) { try { localStorage.setItem("fp_map_view", JSON.stringify(L.view)); } catch { /* noop */ } setMapKey((k) => k + 1); }
      }
    }).catch(() => {}).finally(() => { layersLoadedRef.current = true; });
  }, []);
  useEffect(() => {
    try { localStorage.setItem("fp_map_layers", JSON.stringify({ rx: rxLayer, egib: egibLayer, gesut })); } catch { /* noop */ }
    if (!layersLoadedRef.current) return;
    clearTimeout(layersSaveTimer.current);
    layersSaveTimer.current = setTimeout(() => {
      let view = null; try { view = JSON.parse(localStorage.getItem("fp_map_view") || "null"); } catch { /* noop */ }
      api.put("/auth/me/preferences", { map_layers: { rx: rxLayer, egib: egibLayer, gesut, base, view } }).catch(() => {});
    }, 800);
  }, [rxLayer, egibLayer, gesut, base]);
  const GESUT_MEDIA = [
    { key: "gaz", label: "Gaz", layers: "przewod_gazowy", color: "#EAB308" },
    { key: "energia", label: "Energia", layers: "przewod_elektroenergetyczny", color: "#EF4444" },
    { key: "telekom", label: "Telekom", layers: "przewod_telekomunikacyjny", color: "#F97316" },
    { key: "wodkan", label: "Wod-kan", layers: "przewod_wodociagowy,przewod_kanalizacyjny", color: "#3B82F6" },
    { key: "cieplo", label: "Ciepło", layers: "przewod_cieplowniczy", color: "#EC4899" },
  ];
  const gesutLayers = GESUT_MEDIA.filter((mm) => gesut[mm.key]).map((mm) => mm.layers).join(",");
  const gesutActive = !!gesutLayers;
  const [editItem, setEditItem] = useState(null); // {module, item}
  const [editLine, setEditLine] = useState(null); // {module, id, geometry}
  const [taskRouteGeo, setTaskRouteGeo] = useState(null); // trasa kabla dla zlecenia (z terminarza)
  const [trays, setTrays] = useState(null); // closure
  const [splitterView, setSplitterView] = useState(null); // splitter
  const [odfView, setOdfView] = useState(null); // patchpanel
  const [oltView, setOltView] = useState(null); // olt
  const [switchView, setSwitchView] = useState(null); // switch
  const [closureView, setClosureView] = useState(null); // mufa (rich)
  const [cabinetView, setCabinetView] = useState(null); // szafa
  const [schematic, setSchematic] = useState(false);

  const [panel, setPanel] = useState(null); // reports | ai | logs
  const [settingsTab, setSettingsTab] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showClientImport, setShowClientImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [addressSearching, setAddressSearching] = useState(false);
  const [visibleModules, setVisibleModules] = useState(() => { try { return JSON.parse(localStorage.getItem("fp_visible_modules") || "{}"); } catch { return {}; } });
  const [coverageFilter, setCoverageFilter] = useState("all");
  const [tour, setTour] = useState(null);
  const [showLayers, setShowLayers] = useState(false);
  const [mapCenter, setMapCenter] = useState([52.2297, 21.0122]);
  const [autoplanPreview, setAutoplanPreview] = useState(null);
  const [autoplanPickSource, setAutoplanPickSource] = useState(false);
  const [autoplanEditMufy, setAutoplanEditMufy] = useState(false);
  const [autoplanShowDrops, setAutoplanShowDrops] = useState(true);
  const [autoplanDropLimit, setAutoplanDropLimit] = useState(80);
  const [autoplanBuildings, setAutoplanBuildings] = useState([]);
  const [autoplanEditRoute, setAutoplanEditRoute] = useState(false);
  const [autoplanStreetMode, setAutoplanStreetMode] = useState(false);
  const [autoplanStreet, setAutoplanStreet] = useState([]);
  const [autoplanBusy, setAutoplanBusy] = useState(false);
  const [autoplanUndo, setAutoplanUndo] = useState([]);
  const [autoplanRedo, setAutoplanRedo] = useState([]);
  const autoplanPreviewRef = useRef(null); autoplanPreviewRef.current = autoplanPreview;
  const autoplanEditRouteRef = useRef(false); autoplanEditRouteRef.current = autoplanEditRoute;
  useEffect(() => { window.dispatchEvent(new CustomEvent("fp-autoplan-history", { detail: { canUndo: autoplanUndo.length > 0, canRedo: autoplanRedo.length > 0 } })); }, [autoplanUndo, autoplanRedo]);
  const pushHistory = (prevPlan) => { if (prevPlan) { setAutoplanUndo((h) => [...h.slice(-30), prevPlan]); setAutoplanRedo([]); } };
  const undoAutoplan = () => setAutoplanUndo((h) => { if (!h.length) return h; const prev = h[h.length - 1]; setAutoplanRedo((r) => [...r, autoplanPreviewRef.current]); setAutoplanPreview(prev); window.dispatchEvent(new CustomEvent("fp-autoplan-plan-updated", { detail: prev })); return h.slice(0, -1); });
  const redoAutoplan = () => setAutoplanRedo((r) => { if (!r.length) return r; const nx = r[r.length - 1]; setAutoplanUndo((h) => [...h, autoplanPreviewRef.current]); setAutoplanPreview(nx); window.dispatchEvent(new CustomEvent("fp-autoplan-plan-updated", { detail: nx })); return r.slice(0, -1); });
  const recomputeTrunkTotals = (plan) => {
    const segs = plan.segments || [];
    const total = Math.round(segs.reduce((acc, s) => acc + (s.length_m || 0), 0) * 10) / 10;
    const byFiber = {};
    segs.forEach((s) => { const f = s.fiber_count || 0; if (!byFiber[f]) byFiber[f] = { fiber_count: f, segments: 0, length_m: 0 }; byFiber[f].segments += 1; byFiber[f].length_m += (s.length_m || 0); });
    const cables = Object.values(byFiber).sort((x, y) => x.fiber_count - y.fiber_count).map((c) => ({ ...c, length_m: Math.round(c.length_m * 10) / 10 }));
    const bom = { ...(plan.summary?.bom || {}), cables, total_cable_length_m: total };
    const opt0 = plan.summary?.optical || {};
    let optical = opt0;
    if (opt0.fixed_db != null && opt0.fiber_db_km != null) {
      const childrenMap = {};
      segs.forEach((s) => { const f = s.from?.label; if (!childrenMap[f]) childrenMap[f] = []; childrenMap[f].push({ to: s.to?.label, len: s.route_length_m || 0 }); });
      let worst = 0; const seen = {}; const stack = [{ label: "Szafa", dist: 0 }];
      while (stack.length) { const { label, dist } = stack.pop(); if (seen[label]) continue; seen[label] = true; if (dist > worst) worst = dist; (childrenMap[label] || []).forEach((c) => stack.push({ label: c.to, dist: dist + c.len })); }
      const atten = Math.round((worst / 1000 * opt0.fiber_db_km + opt0.fixed_db) * 100) / 100;
      const budget = opt0.link_budget_db || 28;
      optical = { ...opt0, worst_attenuation_db: atten, ok: atten <= budget, margin_db: Math.round((budget - atten) * 100) / 100 };
    }
    const r0 = plan.summary?.rules;
    let rules = r0;
    if (r0 && r0.trunk_span_m) {
      const spanBad = segs.filter((x) => (x.route_length_m || 0) > r0.trunk_span_m * 1.02).length;
      rules = { ...r0, span_violations: spanBad, ok: spanBad === 0 && (r0.gap_violations || 0) === 0 };
    }
    return { ...plan, summary: { ...(plan.summary || {}), total_length_m: total, optical, bom, rules } };
  };
  const onAutoplanSegmentReroute = async (segIndex, latlng) => {
    const plan = autoplanPreview;
    if (!plan || !plan.segments || !plan.segments[segIndex]) return;
    const cfg = autoplanEditRoute || {};
    const seg = plan.segments[segIndex];
    setAutoplanBusy({ on: true, label: "Przeliczam trasę…" });
    try {
      const { data } = await api.post("/autoplan/reroute-segment", {
        a: { lat: seg.from.lat, lng: seg.from.lng }, b: { lat: seg.to.lat, lng: seg.to.lng },
        via: { lat: latlng.lat, lng: latlng.lng }, pole_link_m: cfg.pole_link_m || 220, trunk_span_m: cfg.trunk_span_m || 0, building_clearance_m: cfg.building_clearance_m || 0,
      });
      const segments = plan.segments.map((x, idx) => (idx === segIndex ? { ...x, geometry: data.geometry, route_length_m: data.length_m, route_type: data.route_type, length_m: Math.round((data.length_m + (x.slack_m || 0)) * 10) / 10 } : x));
      const np = recomputeTrunkTotals({ ...plan, segments });
      pushHistory(plan);
      setAutoplanPreview(np);
      window.dispatchEvent(new CustomEvent("fp-autoplan-plan-updated", { detail: np }));
      toast.success(t("Trasa przeliczona przez wskazany punkt"), { duration: 1500 });
    } catch { toast.error(t("Nie udało się przeliczyć trasy")); } finally { setAutoplanBusy(false); }
  };
  const onAutoplanClosureRemove = (index) => {
    const n = autoplanPreview?.nodes?.[index];
    if (n) window.dispatchEvent(new CustomEvent("fp-autoplan-remove-mufa", { detail: { lat: n.lat, lng: n.lng } }));
  };
  const onAutoplanClosureMove = async (index, latlng) => {
    const plan = autoplanPreview;
    if (!plan || !plan.nodes) return;
    const moved = plan.nodes[index];
    if (!moved) return;
    const affected = [];
    (plan.segments || []).forEach((s, si) => {
      if (s.from.label === moved.label) affected.push({ si, role: "from", other: { lat: s.to.lat, lng: s.to.lng } });
      else if (s.to.label === moved.label) affected.push({ si, role: "to", other: { lat: s.from.lat, lng: s.from.lng } });
    });
    try {
      const { data } = await api.post("/autoplan/reroute", { point: { lat: latlng.lat, lng: latlng.lng }, snap: true, ends: affected.map((a) => a.other) });
      const pt = data.point; // {lat,lng,mount}
      const nodes = plan.nodes.map((n, i) => (i === index ? { ...n, lat: pt.lat, lng: pt.lng, mount: pt.mount } : n));
      const segments = plan.segments.map((x) => ({ ...x }));
      affected.forEach((a, k) => {
        const r = data.routes[k];
        const seg = segments[a.si];
        const geom = (r.geometry || []).map((p) => [p[0], p[1]]); // oriented moved(point)->other
        if (a.role === "from") { seg.from = { ...seg.from, lat: pt.lat, lng: pt.lng }; seg.geometry = geom; }
        else { seg.to = { ...seg.to, lat: pt.lat, lng: pt.lng }; seg.geometry = geom.slice().reverse(); }
        seg.route_length_m = r.length_m;
        seg.route_type = r.route_type;
        seg.length_m = Math.round((r.length_m + (seg.slack_m || 0)) * 10) / 10;
      });
      const np = recomputeTrunkTotals({ ...plan, nodes, segments });
      pushHistory(plan);
      setAutoplanPreview(np);
      window.dispatchEvent(new CustomEvent("fp-autoplan-plan-updated", { detail: np }));
      if (pt.mount && pt.mount !== "teren") toast.success(`${t("Mufa osadzona na")}: ${pt.mount}`, { duration: 1500 });
    } catch {
      // fallback: korekta wizualna końców gdy re-trasowanie się nie powiedzie
      const nodes = plan.nodes.map((n, i) => (i === index ? { ...n, lat: latlng.lat, lng: latlng.lng } : n));
      const segments = (plan.segments || []).map((s) => {
        const ns = { ...s }; let g = (s.geometry || []).slice();
        if (s.from.label === moved.label) { ns.from = { ...s.from, lat: latlng.lat, lng: latlng.lng }; if (g.length) g[0] = [latlng.lat, latlng.lng]; }
        if (s.to.label === moved.label) { ns.to = { ...s.to, lat: latlng.lat, lng: latlng.lng }; if (g.length) g[g.length - 1] = [latlng.lat, latlng.lng]; }
        if (g.length) ns.geometry = g; return ns;
      });
      const np = { ...plan, nodes, segments };
      setAutoplanPreview(np);
      window.dispatchEvent(new CustomEvent("fp-autoplan-plan-updated", { detail: np }));
    }
  };
  useEffect(() => {
    const onPrev = (e) => { const p = e.detail || null; setAutoplanPreview(p); setAutoplanUndo([]); setAutoplanRedo([]); if (p && p.nodes && p.nodes[0]) setFlyTarget({ lat: p.nodes[0].lat, lng: p.nodes[0].lng }); };
    const onPick = () => { setAutoplanPickSource(true); toast.message(t("Kliknij na mapie miejsce szafy dystrybucyjnej")); };
    window.addEventListener("fp-autoplan-preview", onPrev);
    window.addEventListener("fp-autoplan-pick-source", onPick);
    const onEdit = (e) => setAutoplanEditMufy(!!e.detail);
    window.addEventListener("fp-autoplan-edit-mufy", onEdit);
    const onDrops = (e) => setAutoplanShowDrops(!!e.detail);
    window.addEventListener("fp-autoplan-show-drops", onDrops);
    const onDropLimit = (e) => setAutoplanDropLimit(Number(e.detail) || 0);
    window.addEventListener("fp-autoplan-drop-limit", onDropLimit);
    const onBld = (e) => setAutoplanBuildings(Array.isArray(e.detail) ? e.detail : []);
    window.addEventListener("fp-autoplan-buildings", onBld);
    const onEditRoute = (e) => setAutoplanEditRoute(e.detail?.on ? e.detail : false);
    window.addEventListener("fp-autoplan-edit-route", onEditRoute);
    const onStreetMode = (e) => setAutoplanStreetMode(!!e.detail?.on);
    window.addEventListener("fp-autoplan-street-mode", onStreetMode);
    const onStreetClear = () => setAutoplanStreet([]);
    window.addEventListener("fp-autoplan-street-clear", onStreetClear);
    const onBusy = (e) => setAutoplanBusy(e.detail?.on ? { on: true, label: e.detail.label } : false);
    window.addEventListener("fp-autoplan-busy", onBusy);
    const onUndo = () => undoAutoplan();
    const onRedo = () => redoAutoplan();
    window.addEventListener("fp-autoplan-undo", onUndo);
    window.addEventListener("fp-autoplan-redo", onRedo);
    const onKey = (ev) => { if (!autoplanEditRouteRef.current) return; const z = ev.key === "z" || ev.key === "Z"; const y = ev.key === "y" || ev.key === "Y"; if ((ev.ctrlKey || ev.metaKey) && z && !ev.shiftKey) { ev.preventDefault(); undoAutoplan(); } else if ((ev.ctrlKey || ev.metaKey) && (y || (z && ev.shiftKey))) { ev.preventDefault(); redoAutoplan(); } };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("fp-autoplan-preview", onPrev); window.removeEventListener("fp-autoplan-pick-source", onPick); window.removeEventListener("fp-autoplan-edit-mufy", onEdit); window.removeEventListener("fp-autoplan-show-drops", onDrops); window.removeEventListener("fp-autoplan-drop-limit", onDropLimit); window.removeEventListener("fp-autoplan-buildings", onBld); window.removeEventListener("fp-autoplan-edit-route", onEditRoute); window.removeEventListener("fp-autoplan-street-mode", onStreetMode); window.removeEventListener("fp-autoplan-street-clear", onStreetClear); window.removeEventListener("fp-autoplan-busy", onBusy); window.removeEventListener("fp-autoplan-undo", onUndo); window.removeEventListener("fp-autoplan-redo", onRedo); window.removeEventListener("keydown", onKey); };
  }, [t]);

  const [powerStatus, setPowerStatus] = useState({});
  const [showAddresses, setShowAddresses] = useState(false);
  const [snmpDevices, setSnmpDevices] = useState([]);
  const [showWifi, setShowWifi] = useState(() => localStorage.getItem("fp_show_wifi") !== "0");
  const [wifiData, setWifiData] = useState(null);
  const loadWifi = useCallback(async () => {
    const [sites, devices, links] = await Promise.all(["sites", "devices", "links"].map((k) => api.get(`/wifi/${k}`).then((r) => r.data).catch(() => [])));
    setWifiData({ sites, devices, links });
  }, []);
  useEffect(() => { loadWifi(); }, [loadWifi]);
  const [showSnmp, setShowSnmp] = useState(true);
  const [monterLocs, setMonterLocs] = useState([]);
  const [showMonters, setShowMonters] = useState(false);
  const [monterTrack, setMonterTrack] = useState([]);
  const isManager = user?.role === "admin" || user?.role === "projektant";
  useEffect(() => { if (user?.department === "ksiegowosc") setActive("clients"); }, [user]);
  useEffect(() => { api.get("/optical/splitter-power").then((r) => setSplitterPower(r.data || {})).catch(() => {}); }, []);
  const [snmpInitial, setSnmpInitial] = useState(null);
  const [snmpPickMode, setSnmpPickMode] = useState(false);
  const [snmpPicked, setSnmpPicked] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [addrCount, setAddrCount] = useState(0);
  const [addrCapped, setAddrCapped] = useState(false);
  const [fitBounds, setFitBounds] = useState(null);
  const [addInitial, setAddInitial] = useState(null);
  const [slackHost, setSlackHost] = useState(null); // {module, item, cables}
  const [slackProposal, setSlackProposal] = useState(null); // {cable, hints}
  const viewRef = useRef(null);

  const openAddSlack = async ({ module, item }) => {
    if (item.lat == null || item.lng == null) { toast.error(t("Obiekt bez lokalizacji")); return; }
    try {
      const { data: r } = await api.get(`/passing/${module}/${item.id}`, { params: { radius: 30 } });
      setSlackHost({ module, item, cables: r.cables || [] });
    } catch {
      setSlackHost({ module, item, cables: [] });
    }
  };

  const connectClientFromAddress = (a) => {
    setActive(null); setDetail(null); setPanel(null);
    setAddInitial({ address: [a.label, a.locality].filter(Boolean).join(", ") });
    setAddMode("clients"); setPendingCoords([a.lat, a.lng]);
    toast.info(t("Uzupełnij dane klienta — lokalizacja i adres wypełnione z bazy adresowej."));
  };

  const coverageFromAddress = (a) => {
    setActive(null); setDetail(null); setPanel(null);
    setAddInitial({ miejscowosc: a.locality || "", ulica: a.street || "", nr_budynku: a.number || "",
                    simc: a.simc || "", wojewodztwo: a.wojewodztwo || "", powiat: a.powiat || "",
                    gmina: a.gmina || "", kod_ulic: a.kod_ulic || "" });
    setAddMode("coverage"); setPendingCoords([a.lat, a.lng]);
    toast.info(t("Uzupełnij dane zasięgu — lokalizacja i adres wypełnione z bazy adresowej."));
  };

  const toggleModule = (k) => setVisibleModules((v) => { const nv = { ...v, [k]: v[k] === false ? true : false }; try { localStorage.setItem("fp_visible_modules", JSON.stringify(nv)); } catch { /* noop */ } return nv; });
  const toggleGisLayer = (id) => setLayers((ls) => ls.map((l) => l.id === id ? { ...l, visible: l.visible === false ? true : false } : l));

  const loadAll = useCallback(async () => {
    const results = await Promise.all(MODULE_ORDER.map((k) => api.get(`/${k}`).then((r) => [k, r.data]).catch(() => [k, []])));
    setData(Object.fromEntries(results));
    loadFolders();
    api.get("/gis-layers").then((r) => setLayers(r.data)).catch(() => {});
    api.get("/device-links").then((r) => setDeviceLinks(r.data.links || [])).catch(() => {});
    api.get("/snmp/devices").then((r) => setSnmpDevices(r.data || [])).catch(() => {});
    api.get("/optical/clients-status").then((r) => setPowerStatus(Object.fromEntries(r.data.map((x) => [x.id, x.status])))).catch(() => {});
  }, [loadFolders]);

  const loadSnmp = useCallback(() => api.get("/snmp/devices").then((r) => setSnmpDevices(r.data || [])).catch(() => {}), []);

  useEffect(() => {
    api.get("/optical/clients-status").then((r) => setPowerStatus(Object.fromEntries(r.data.map((x) => [x.id, x.status])))).catch(() => {});
  }, [settings]);

  useEffect(() => { api.get("/addresses/count").then((r) => setAddrCount(r.data.count)).catch(() => {}); }, []);

  const fetchAddresses = useCallback(async (view) => {
    if (!view) return;
    try {
      const { data: pts } = await api.get("/addresses/in-view", { params: {
        min_lat: view.bounds.south, min_lng: view.bounds.west,
        max_lat: view.bounds.north, max_lng: view.bounds.east, limit: 4000 } });
      setAddresses(pts);
      setAddrCapped(pts.length >= 4000);
    } catch { setAddresses([]); }
  }, []);

  const onViewChange = useCallback((view) => {
    viewRef.current = view;
    if (showAddresses) fetchAddresses(view);
  }, [showAddresses, fetchAddresses]);

  useEffect(() => {
    if (showAddresses) {
      fetchAddresses(viewRef.current);
      api.get("/addresses/count").then((r) => setAddrCount(r.data.count)).catch(() => {});
      api.get("/addresses/bounds").then((r) => {
        if (!r.data.count) toast.warning(t("Baza adresowa jest pusta — wgraj plik w Ustawieniach."));
      }).catch(() => {});
    } else { setAddresses([]); setAddrCapped(false); }
  }, [showAddresses, fetchAddresses]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live monter locations layer (managers only, poll while enabled).
  useEffect(() => {
    if (!isManager || !showMonters) { setMonterLocs([]); return; }
    const fetchLocs = () => api.get("/schedule/monters/locations").then((r) => setMonterLocs(r.data || [])).catch(() => {});
    fetchLocs();
    const id = setInterval(fetchLocs, 15000);
    return () => clearInterval(id);
  }, [isManager, showMonters]);

  const loadMonterTrack = useCallback((mid) => {
    api.get(`/schedule/monters/${mid}/track?work_day=1`).then((r) => {
      const pts = (r.data || []).map((p) => [p.lat, p.lng]);
      if (pts.length < 2) { toast.info(t("Brak zapisanej trasy z dzisiejszego czasu pracy tego montera.")); }
      setMonterTrack(pts);
      if (pts.length) setFlyTarget({ lat: pts[pts.length - 1][0], lng: pts[pts.length - 1][1] });
    }).catch(() => toast.error(t("Nie udało się pobrać trasy.")));
  }, [t]);

  // Refresh SNMP device statuses for the map layer.
  useEffect(() => {
    const secs = Math.max(15, Number(settings.snmp_refresh_interval) || 30);
    const id = setInterval(loadSnmp, secs * 1000);
    return () => clearInterval(id);
  }, [settings.snmp_refresh_interval, loadSnmp]);

  // Auto-logout after 15 min of inactivity (warning ~1 min before)
  useEffect(() => {
    let warnTimer, logoutTimer;
    const reset = () => {
      clearTimeout(warnTimer); clearTimeout(logoutTimer);
      warnTimer = setTimeout(() => toast.warning(t("Zostaniesz wylogowany za 1 minutę z powodu bezczynności.")), 14 * 60 * 1000);
      logoutTimer = setTimeout(() => { toast.error(t("Wylogowano z powodu bezczynności.")); logout(); }, 15 * 60 * 1000);
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => reset();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    reset();
    return () => { clearTimeout(warnTimer); clearTimeout(logoutTimer); events.forEach((e) => window.removeEventListener(e, handler)); };
  }, [logout]);

  // Deep link from QR: ?open=<module>&id=<id>
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const mod = p.get("open"), id = p.get("id");
    if (mod && id && data[mod]) {
      const it = data[mod].find((x) => x.id === id);
      if (it) {
        setActive(mod);
        setDetail({ module: mod, item: it });
        if (it.lat != null) setFlyTarget({ lat: it.lat, lng: it.lng });
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, [data]);

  // Połączenie przychodzące → przybliż mapę i podświetl klienta (jeśli włączone)
  useEffect(() => {
    const onIncoming = (e) => {
      if (settings?.sip?.call_flyto === false) return;
      const d = (e && e.detail) || {};
      if (d.lat != null && d.lng != null) {
        setFlyTarget({ lat: Number(d.lat), lng: Number(d.lng) });
        if (d.id) {
          setSpotlight({ module: "clients", id: d.id, isolate: false });
          setTimeout(() => setSpotlight((s) => (s && !s.isolate && s.id === d.id ? null : s)), 20000);
        }
      }
    };
    const onEnd = () => setSpotlight((s) => (s && !s.isolate && s.module === "clients" ? null : s));
    window.addEventListener("fp-sip-incoming", onIncoming);
    window.addEventListener("fp-sip-callend", onEnd);
    return () => { window.removeEventListener("fp-sip-incoming", onIncoming); window.removeEventListener("fp-sip-callend", onEnd); };
  }, [settings]);

  const menuVariant = user?.menu_style || settings.menu_style || "left";
  const [showSms, setShowSms] = useState(false);
  const [smsUnread, setSmsUnread] = useState(0);
  const [pendingRenewals, setPendingRenewals] = useState(0);
  const prevUnreadRef = useRef(null);
  const prevFaultsRef = useRef(null);
  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    const load = () => api.get("/license/pending-count").then((r) => setPendingRenewals(r.data?.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user]);
  const fireDesktopNotify = (title, body, onClick) => {
    try {
      const isDesktop = /electron/i.test(navigator.userAgent) || !!window.fiberpass;
      if (!isDesktop || typeof Notification === "undefined") return;
      const n = new Notification(title, { body: body || "", silent: false });
      n.onclick = () => { try { window.fiberpass?.focus?.(); } catch { /* noop */ } if (onClick) onClick(); };
    } catch { /* noop */ }
  };
  const playSmsBeep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 880;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
      o.onended = () => { try { ctx.close(); } catch { /* noop */ } };
    } catch { /* noop */ }
  };
  const refreshUnread = useCallback(() => {
    api.get("/sms/inbox/unread-count").then((r) => {
      const c = r.data?.count || 0;
      setSmsUnread(c);
      const prev = prevUnreadRef.current;
      if (prev !== null && c > prev && r.data?.latest) {
        const m = r.data.latest;
        playSmsBeep();
        toast.info(`${t("Nowy SMS")}: ${m.sender || ""}`, { description: (m.text || "").slice(0, 90), action: { label: t("Otwórz"), onClick: () => setShowSms(true) } });
        fireDesktopNotify(`${t("Nowy SMS")}: ${m.sender || ""}`, (m.text || "").slice(0, 120), () => setShowSms(true));
      }
      prevUnreadRef.current = c;
    }).catch(() => {});
  }, [t]); // eslint-disable-line
  useEffect(() => { refreshUnread(); const id = setInterval(refreshUnread, 15000); return () => clearInterval(id); }, [refreshUnread]);
  const refreshFaults = useCallback(() => {
    api.get("/faults-active-count").then((r) => {
      const c = r.data?.count || 0;
      const prev = prevFaultsRef.current;
      if (prev !== null && c > prev && r.data?.latest) {
        const f = r.data.latest;
        playSmsBeep();
        toast.warning(`${t("Nowa awaria")}: ${f.title || ""}`, { description: f.ticket_no ? `#${f.ticket_no}` : "", action: { label: t("Otwórz"), onClick: () => setPanel("faults") } });
        fireDesktopNotify(`${t("Nowa awaria")}: ${f.title || ""}`, f.ticket_no ? `${t("Zgłoszenie")} #${f.ticket_no}` : "", () => setPanel("faults"));
      }
      prevFaultsRef.current = c;
    }).catch(() => {});
  }, [t]); // eslint-disable-line
  useEffect(() => { refreshFaults(); const id = setInterval(refreshFaults, 20000); return () => clearInterval(id); }, [refreshFaults]);
  const openSms = () => setShowSms(true);
  const togglePanel = (name) => { setActive(null); setPanel((p) => (p === name ? null : name)); };
  const [wifiNav, setWifiNav] = useState(null);
  const openWifi = (tab) => {
    if (!tab) { togglePanel("wifi"); return; }
    setActive(null); setWifiNav({ tab, n: Date.now() }); setPanel("wifi");
  };

  const openModule = (key) => {
    if (key === "faults") { setActive(null); setPanel((p) => (p === "faults" ? null : "faults")); return; }
    setPanel(null);
    setActive((cur) => (cur === key ? null : key));
  };

  const runAction = (id) => {
    if (id === "global:close") {
      if (detail) return setDetail(null);
      if (fiberTrace) { setFiberTrace(null); return setTracePath(null); }
      if (trace) { setTrace(null); return setTracePath(null); }
      if (panel) return setPanel(null);
      if (active) return setActive(null);
      if (showImport) return setShowImport(false);
      if (showExport) return setShowExport(false);
      return;
    }
    if (id === "global:fullscreen") return window.dispatchEvent(new Event("fp-fullscreen-toggle"));
    if (id === "action:import") return setShowImport(true);
    if (id === "action:export") return setShowExport(true);
    const [k, key] = id.split(":");
    if (k === "panel") return togglePanel(key);
    if (k === "module") return openModule(key);
  };

  useEffect(() => {
    let list = loadShortcuts();
    const reload = () => { list = loadShortcuts(); };
    const onKey = (e) => {
      const el = e.target;
      if (el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      const combo = normalizeCombo(e);
      const hit = list.find((s) => s.combo === combo);
      if (hit) { e.preventDefault(); return runAction(hit.action); }
      if (combo === "escape") { e.preventDefault(); return runAction("global:close"); }
      if (combo === "f") { e.preventDefault(); return runAction("global:fullscreen"); }
    };
    window.addEventListener("fp-shortcuts-changed", reload);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("fp-shortcuts-changed", reload); window.removeEventListener("keydown", onKey); };
  }, [detail, trace, fiberTrace, panel, active, showImport, showExport]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMapClick = (pt) => {
    if (addMode) { setPendingCoords(pt); }
  };

  // While in "add" mode, clicking an existing feature (e.g. a pole) places the
  // new record at that feature's location instead of opening its list.
  const onFeaturePick = ({ module, item }) => {
    if (!addMode) { setActive(module); return; }
    if (item.lat == null || item.lng == null) return;
    setPendingCoords([item.lat, item.lng]);
    if (module === "poles") {
      setAddInitial((prev) => ({ ...(prev || {}), pole_number: item.number || "", pole_id: item.id }));
      toast.success(`${t("Umieszczono na słupie")} ${item.number || ""}`.trim(), { duration: 1200 });
    }
  };

  const hav = (a, b) => {
    const R = 6371000, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  const snapCandidates = () => {
    const arr = [];
    ["poles", "closures", "splitters", "olt", "odf"].forEach((k) =>
      (data[k] || []).forEach((it) => { if (it.lat != null) arr.push([it.lat, it.lng]); }));
    return arr;
  };

  // While drawing a client's auto-route, prompt when a point lands within 7 m of a
  // closure/splitter (a "mufa ze spliterem") so the user can choose to connect.
  const maybeDetectSplitter = (pos) => {
    if (!clientRoute || splitterConnect) return;
    let best = null;
    for (const s of (data.splitters || [])) {
      if (declinedSplitters.includes(s.id)) continue;
      const c = s.closure_id ? (data.closures || []).find((x) => x.id === s.closure_id) : null;
      const p = c && c.lat != null ? [c.lat, c.lng] : (s.lat != null ? [s.lat, s.lng] : null);
      if (!p) continue;
      const d = hav(pos, p);
      if (d <= 7 && (best === null || d < best.d)) best = { d, splitter: s, closure: c, pos: p };
    }
    if (best) { setBlinkSplitterId(best.splitter.id); setSplitterConnect(best); }
  };

  const onDrawClick = (pt) => {
    if (snapOff) {
      const np = { lat: pt[0], lng: pt[1], snapped: false };
      setDrawPoints((prev) => [...prev, np]);
      maybeDetectSplitter([np.lat, np.lng]);
      return;
    }
    let best = null;
    for (const c of snapCandidates()) {
      const d = hav(pt, c);
      if (best === null || d < best.d) best = { d, c };
    }
    const snapped = best && best.d < 40;
    const p = snapped ? { lat: best.c[0], lng: best.c[1], snapped: true } : { lat: pt[0], lng: pt[1], snapped: false };
    setDrawPoints((prev) => [...prev, p]);
    if (snapped) toast.success(t("Przyciągnięto do infrastruktury"), { duration: 900 });
    maybeDetectSplitter([p.lat, p.lng]);
  };

  const lineLenM = (coords) => {
    const R = 6371000, rad = (x) => (x * Math.PI) / 180;
    let s = 0;
    for (let i = 1; i < (coords || []).length; i++) {
      const a = coords[i - 1], b = coords[i];
      const dla = rad(b[0] - a[0]), dlo = rad(b[1] - a[1]);
      const h = Math.sin(dla / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dlo / 2) ** 2;
      s += R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    }
    return s;
  };
  const onExtendCable = async (item, end) => {
    const geo = item.geometry || [];
    if (geo.length < 2) { toast.error(t("Kabel nie ma zapisanej geometrii trasy")); return; }
    let breakLinks = false;
    try {
      const { data: sum } = await api.get(`/cables/${item.id}/link-summary`);
      if (sum.items?.length) {
        if (!window.confirm(`${t("Uwaga — przedłużenie kabla może naruszyć istniejące połączenia:")}\n\n• ${sum.items.join("\n• ")}\n\n${t("Kontynuować?")}`)) return;
        breakLinks = window.confirm(`${t("Czy usunąć te powiązania (spawy/połączenia)?")}\n\nOK = ${t("usuń powiązania")}\n${t("Anuluj")} = ${t("zachowaj (tylko przedłuż)")}`);
      }
    } catch { /* brak podsumowania nie blokuje */ }
    const anchor = end === "start" ? geo[0] : geo[geo.length - 1];
    setEditItem(null);
    setExtendCtx({ cableId: item.id, end, breakLinks, baseLength: lineLenM(geo) });
    setDrawMode("extend_cable");
    setDrawPoints([{ lat: anchor[0], lng: anchor[1], snapped: true }]);
    toast.message(t("Klikaj kolejne punkty trasy (po słupach lub dowolnie), potem „Zakończ i zapisz”."), { duration: 4000 });
  };

  const finishDraw = () => {
    if (drawMode === "extend_cable") {
      const pts = drawPoints.map((p) => [p.lat, p.lng]).slice(1);
      if (!pts.length) { toast.error(t("Dodaj przynajmniej jeden nowy punkt trasy")); return; }
      (async () => {
        try {
          const { data } = await api.post(`/cables/${extendCtx.cableId}/extend`, { end: extendCtx.end, points: pts, break_links: extendCtx.breakLinks });
          toast.success(`${t("Przedłużono kabel — nowa długość")}: ${data.length_m} m${data.broken?.length ? " · " + t("usunięto") + ": " + data.broken.join(", ") : ""}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        setExtendCtx(null); setDrawMode(null); setDrawPoints([]); loadAll();
      })();
      return;
    }
    const minPts = drawMode === "zones" ? 3 : 2;
    if (drawPoints.length < minPts) { toast.error(drawMode === "zones" ? t("Zaznacz min. 3 punkty strefy") : t("Zaznacz min. 2 punkty trasy")); return; }
    const geometry = drawPoints.map((p) => [p.lat, p.lng]);
    let len = 0;
    for (let i = 1; i < geometry.length; i++) len += hav(geometry[i - 1], geometry[i]);
    if (drawMode === "client_cables") {
      if (clientRoute) {
        (async () => {
          try {
            await api.post("/client_cables", { number: `KA-${(clientRoute.clientName || "klient").slice(0, 12)}`, client_id: clientRoute.clientId, geometry, length_m: Math.round(len) });
            toast.success(t("Zapisano kabel kliencki (bez splittera)"));
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          resetClientRoute(); loadAll();
        })();
        return;
      }
      setClientCableGeo({ geometry, length: Math.round(len) });
      setDrawMode(null); setClientCableDraw(false); setBlinkSplitterId(null); setDrawPoints([]);
      toast.success(t("Trasa kabla narysowana"));
      return;
    }
    if (drawMode === "task_cable_route") {
      setTaskRouteGeo({ geometry, length: Math.round(len) });
      setDrawMode(null); setDrawPoints([]);
      setPanel("schedule");
      toast.success(t("Trasa kabla narysowana — wróć do zlecenia"));
      return;
    }
    const hints = drawMode === "cables" ? detectSlackHints(drawPoints) : [];
    setDrawGeometry({ module: drawMode, geometry, length: Math.round(len), slackHints: hints });
    setDrawMode(null);
  };

  const startClientCableDraw = ({ splitterId, house }) => {
    setBlinkSplitterId(splitterId || null);
    setDrawMode("client_cables");
    setDrawPoints(house && house[0] != null ? [{ lat: house[0], lng: house[1], snapped: false }] : []);
    setClientCableDraw(true);
    toast.message(t("Klikaj po słupach do migającego splitera, potem „Zakończ i zapisz”"), { duration: 3500 });
  };

  const cancelDraw = () => { setDrawMode(null); setDrawPoints([]); setClientCableDraw(false); setBlinkSplitterId(null); setClientRoute(null); setSplitterConnect(null); setDeclinedSplitters([]); setExtendCtx(null); };

  const resetClientRoute = () => { setClientRoute(null); setDrawMode(null); setDrawPoints([]); setClientCableDraw(false); setBlinkSplitterId(null); setDeclinedSplitters([]); setSplitterConnect(null); };

  // Single source of truth for showing the client route-drawing prompt after a
  // NEW client is saved (kept in one place so both add entry points stay in sync).
  const maybePromptClientRoute = (saved, wasNew) => {
    if (wasNew && saved && saved.id && saved.lat != null && !saved.splitter_id) setClientRoutePrompt(saved);
  };

  // New flow: after saving a client, draw its drop cable starting at the house address.
  const startClientRouteDraw = (client) => {
    setClientRoutePrompt(null);
    setClientRoute({ clientId: client.id, clientName: client.name, house: [client.lat, client.lng] });
    setDeclinedSplitters([]); setSplitterConnect(null);
    setDrawMode("client_cables"); setClientCableDraw(true);
    setDrawPoints([{ lat: client.lat, lng: client.lng, snapped: false }]);
    toast.message(t("Klikaj punkty trasy po słupach. Gdy dojdziesz do mufy ze spliterem — zapytam o podłączenie."), { duration: 4500 });
  };

  const declineSplitter = () => {
    if (splitterConnect) setDeclinedSplitters((d) => [...d, splitterConnect.splitter.id]);
    setSplitterConnect(null); setBlinkSplitterId(null);
    toast.message(t("Pomiń — rysuj dalej do innej mufy"), { duration: 2000 });
  };

  const finalizeConnect = async (port) => {
    if (!splitterConnect || !clientRoute) return;
    const geom = drawPoints.map((p) => [p.lat, p.lng]);
    if (splitterConnect.pos) geom.push([splitterConnect.pos[0], splitterConnect.pos[1]]);
    try {
      const { data: r } = await api.post(`/clients/${clientRoute.clientId}/connect-splitter`, { splitter_id: splitterConnect.splitter.id, splitter_port: port, geometry: geom });
      toast.success(`${t("Podłączono do splittera")} ${r.splitter_number || ""} · ${t("port")} ${port}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return; }
    resetClientRoute(); loadAll();
  };

  // Detect "zawroty" (hairpin backtracks) along a drawn cable — where the route
  // folds back on itself near a mufa/słup, indicating a physical cable reserve (slack).
  const detectSlackHints = (pts) => {
    const poles = data.poles || [];
    const closures = data.closures || [];
    const nearest = (p, arr) => {
      let best = null;
      for (const o of arr) { if (o.lat == null) continue; const d = hav([p.lat, p.lng], [o.lat, o.lng]); if (best === null || d < best.d) best = { d, o }; }
      return best;
    };
    const hints = [];
    for (let i = 1; i < pts.length - 1; i++) {
      const a = [pts[i - 1].lat, pts[i - 1].lng], b = [pts[i].lat, pts[i].lng], c = [pts[i + 1].lat, pts[i + 1].lng];
      const Lin = hav(a, b), Lout = hav(b, c), direct = hav(a, c);
      if (Lin < 1 || Lout < 1) continue;
      const detour = Lin + Lout - direct;
      const v1 = [b[0] - a[0], b[1] - a[1]], v2 = [c[0] - b[0], c[1] - b[1]];
      const m1 = Math.hypot(v1[0], v1[1]), m2 = Math.hypot(v2[0], v2[1]);
      const cos = m1 && m2 ? (v1[0] * v2[0] + v1[1] * v2[1]) / (m1 * m2) : 1;
      const ang = Math.acos(Math.max(-1, Math.min(1, cos))) * 180 / Math.PI; // heading change 0..180
      if (ang >= 135 && detour >= 3) {
        const nc = nearest(pts[i], closures), np = nearest(pts[i], poles);
        let host = null, reserve = "Inny", label = null;
        if (nc && nc.d <= 25) { host = nc.o; reserve = "Mufa"; label = nc.o.number || nc.o.name; }
        else if (np && np.d <= 25) { host = np.o; reserve = "Słup"; label = np.o.number; }
        hints.push({ lat: host ? host.lat : b[0], lng: host ? host.lng : b[1], meters: Math.round(detour), reserve_type: reserve, host_label: label });
      }
    }
    return hints;
  };

  const doTrace = async (client) => {
    try {
      const { data: traceRes } = await api.get(`/trace/${client.id}`);
      setTrace(traceRes); setTracePath(traceRes.path_coords); setPanel(null);
      if (traceRes.path_coords?.length) setFlyTarget({ lat: traceRes.path_coords[0][0], lng: traceRes.path_coords[0][1] });
      toast.success(t("Wyznaczono trasę sygnału"));
    } catch { toast.error(t("Nie udało się wyznaczyć trasy")); }
  };

  const flyToStep = (step) => {
    if (step?.lat == null || step?.lng == null) return;
    setFlyTarget({ lat: step.lat, lng: step.lng });
    if (step.module && step.id) {
      setSpotlight({ module: step.module, id: step.id, isolate: false, blink: true });
      setTimeout(() => setSpotlight((sp) => (sp && sp.blink && sp.id === step.id ? null : sp)), 8000);
    }
  };

  const traceFiber = async (cable, fiberNum) => {
    try {
      const { data } = await api.get(`/cables/${cable.id}/fiber-trace`, { params: { fiber: fiberNum } });
      setFibersCable(null);
      setFiberTrace({ ...data, cableNumber: cable.number, fiber: fiberNum });
      if (data.path_coords?.length > 1) {
        setTracePath(data.path_coords);
        setFlyTarget({ lat: data.path_coords[0][0], lng: data.path_coords[0][1] });
        toast.success(`${t("Trasa włókna")} #${fiberNum} · ${data.total_atten_db} dB`);
      } else {
        setTracePath(null);
        toast(t("Włókno bez geometrii trasy — pokazuję kroki sygnału"));
      }
    } catch { toast.error(t("Nie udało się wyznaczyć trasy włókna")); }
  };

  const seedDemo = async () => {
    try { const { data: r } = await api.post("/seed-demo"); toast.success(r.message); loadAll(); }
    catch { toast.error(t("Błąd ładowania danych demo")); }
  };

  const clearDemo = async () => {
    if (!window.confirm("Usunąć wszystkie dane demonstracyjne? Tej operacji nie można cofnąć.")) return;
    try { const { data: r } = await api.post("/seed-demo/clear"); toast.success(r.message); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || t("Błąd usuwania danych demo")); }
  };

  const pickSearch = (r) => {
    if (r.lat != null) setFlyTarget({ lat: r.lat, lng: r.lng });
    if (data[r.type]) { setActive(r.type); }
  };

  const addressSearch = async (e) => {
    e.preventDefault();
    const q = e.target.elements.addr.value.trim();
    if (!q) return;
    setAddressSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
      const arr = await res.json();
      if (arr[0]) { setFlyTarget({ lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) }); toast.success(t("Znaleziono adres")); }
      else toast.error(t("Nie znaleziono adresu"));
    } catch { toast.error(t("Błąd wyszukiwania adresu")); }
    finally { setAddressSearching(false); }
  };

  const baseBtns = [
    { key: "dark", icon: MapIcon, title: "Ciemna" },
    { key: "osm", icon: Sun, title: "Drogowa (OSM)" },
    ...(tiles.sat ? [{ key: "sat", icon: Satellite, title: "Satelitarna" }] : []),
  ];

  const panelOpen = !!(active || panel || detail || trays || splitterView || fibersCable || trace || fiberTrace);

  // Katalogi: ukryte katalogi znikają z mapy, kolor katalogu = kolor punktu (gdy obiekt nie ma własnego).
  const mapData = useMemo(() => {
    if (!folders.length) return data;
    const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
    const hid = new Set(hiddenFolders);
    const out = {};
    for (const [k, arr] of Object.entries(data)) {
      out[k] = !Array.isArray(arr) ? arr : arr.filter((it) => !(it.folder_id && hid.has(it.folder_id)))
        .map((it) => (it.folder_id && byId[it.folder_id]?.color && !it.color && it.lat != null ? { ...it, color: byId[it.folder_id].color } : it));
    }
    return out;
  }, [data, folders, hiddenFolders]);
  // Widoczność katalogu na mapie jest osobna dla każdego użytkownika/przeglądarki.
  const toggleFolder = (f) => setHiddenFolders((h) => {
    const nh = h.includes(f.id) ? h.filter((x) => x !== f.id) : [...h, f.id];
    try { localStorage.setItem("fp_hidden_folders", JSON.stringify(nh)); } catch { /* noop */ }
    return nh;
  });

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#0F172A]">
      <MapView
        key={mapKey}
        data={mapData} base={tiles[base] ? base : "osm"} tiles={tiles} layers={layers}
        cableColors={settings.cable_colors} markerIcons={settings.marker_icons} markerColors={settings.marker_colors} showCableLength={settings.show_cable_length} markerScale={settings.marker_scale}
        markerSizeType={settings.marker_size_type} markerZoomMin={settings.marker_zoom_min} markerZoomMax={settings.marker_zoom_max} showLegend={settings.show_legend} menuVariant={menuVariant} blinkSplitterId={blinkSplitterId} spotlight={spotlight} splitterPower={showSplitterPower ? splitterPower : null}
        addMode={!!addMode} measureMode={measureMode}
        onMapClick={onMapClick}
        onMeasureClick={(pt) => setMeasurePts((p) => [...p, pt])}
        measurePts={measurePts}
        onSelect={({ module, item }) => { if (item) { setDetail({ module, item }); setSpotlight({ module, id: item.id, isolate: true }); if (item.lat != null) setFlyTarget({ lat: item.lat, lng: item.lng }); } else setActive(module); }}
        tracePath={tracePath} flyTarget={flyTarget}
        pendingMarker={pendingCoords} visibleModules={visibleModules} coverageFilter={coverageFilter}
        deviceLinks={showLinks ? deviceLinks : []}
        drawMode={!!drawMode} drawPoints={drawPoints} onDrawClick={onDrawClick}
        onFeaturePick={onFeaturePick}
        autoplan={autoplanPreview} onAutoplanClosureMove={onAutoplanClosureMove} autoplanEditMufy={autoplanEditMufy} onAutoplanClosureRemove={onAutoplanClosureRemove} autoplanShowDrops={autoplanShowDrops} autoplanDropLimit={autoplanDropLimit} autoplanBuildings={autoplanBuildings} autoplanEditRoute={!!autoplanEditRoute} onAutoplanSegmentReroute={onAutoplanSegmentReroute} autoplanStreet={autoplanStreet} autoplanBusy={autoplanBusy}
        onMoveEnd={setMapCenter}
        powerStatus={powerStatus}
        rxLayer={rxLayer}
        egibLayer={egibLayer}
        gesutLayers={gesutLayers}
        addresses={showAddresses ? addresses : []}
        onViewChange={onViewChange}
        snmpDevices={showSnmp ? snmpDevices : []}
        wifi={showWifi ? wifiData : null}
        monterLocs={showMonters ? monterLocs : []}
        monterTrack={showMonters ? monterTrack : []}
        onMonterTrack={loadMonterTrack}
        onSnmpClick={(id) => { setSnmpInitial({ tab: "live", device: id }); setPanel("snmp"); setActive(null); }}
        pickMode={snmpPickMode || autoplanPickSource || autoplanEditMufy || autoplanStreetMode}
        boxMode={boxMode} boxPts={boxPts}
        onBoxClick={(pt) => { setBoxCount(null); setBoxPts((p) => (p.length >= 2 ? [pt] : [...p, pt])); }}
        boxHighlight={boxHighlight}
        onPickClick={(ll) => {
          if (autoplanStreetMode) { const p = [Number(ll.lat.toFixed(6)), Number(ll.lng.toFixed(6))]; setAutoplanStreet((s) => { const ns = [...s, p]; window.dispatchEvent(new CustomEvent("fp-autoplan-street", { detail: { points: ns } })); return ns; }); return; }
          if (autoplanPickSource) { setAutoplanPickSource(false); window.dispatchEvent(new CustomEvent("fp-autoplan-source-picked", { detail: { lat: Number(ll.lat.toFixed(6)), lng: Number(ll.lng.toFixed(6)) } })); return; }
          if (autoplanEditMufy) { window.dispatchEvent(new CustomEvent("fp-autoplan-add-mufa", { detail: { lat: Number(ll.lat.toFixed(6)), lng: Number(ll.lng.toFixed(6)) } })); return; }
          setSnmpPicked({ lat: Number(ll.lat.toFixed(6)), lng: Number(ll.lng.toFixed(6)) }); setSnmpPickMode(false);
        }}
        onAddressConnect={connectClientFromAddress}
        onAddressCoverage={coverageFromAddress}
        onAddSlack={openAddSlack}
        slackHints={slackProposal ? slackProposal.hints : null}
        fitBounds={fitBounds}
        editLine={editLine}
        onEditLineSave={async (el, geom) => {
          try {
            const { data } = await api.put(`/${el.module}/${el.id}`, { geometry: geom });
            setEditLine(null); loadAll();
            const len = data?.optical_length_m ?? data?.route_length_m;
            toast.success(len != null ? `${t("Zapisano trasę")} · ${(len / 1000).toFixed(2)} km` : t("Zapisano trasę"));
          } catch { toast.error(t("Błąd zapisu trasy")); }
        }}
        onEditLineCancel={() => setEditLine(null)}
      />

      {settings.sip?.enabled && <SipDialer sip={settings.sip} />}
      <button onClick={openSms} data-testid="sms-inbox-fab" title={t("Skrzynka SMS")}
        className="absolute right-0 z-[744] bg-slate-900/90 backdrop-blur border border-r-0 border-slate-700 rounded-l-lg px-1.5 py-3 hover:bg-slate-800 flex flex-col items-center text-cyan-300"
        style={{ top: "calc(50% + 72px)" }}>
        <MessageSquare size={16} />
        {smsUnread > 0 && <span data-testid="sms-unread-badge" className="absolute -top-1.5 -left-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{smsUnread > 99 ? "99+" : smsUnread}</span>}
      </button>
      {showSms && <SmsInboxModal onClose={() => { setShowSms(false); refreshUnread(); }} onChanged={() => { loadAll(); refreshUnread(); }} canEdit={user?.role !== "odczyt"} canDelete={user?.role === "admin" || user?.role === "projektant"} />}
      <Sidebar user={user} menuLayout={user?.menu_layout} active={active} onSelect={openModule} variant={menuVariant} pendingRenewals={pendingRenewals}
        onReports={() => togglePanel("reports")}
        onAI={() => togglePanel("ai")}
        onLogs={() => togglePanel("logs")}
        onQRLabels={() => togglePanel("qrlabels")}
        onOtdr={() => togglePanel("otdr")}
        onUsers={() => togglePanel("users")}
        onSecurity={() => togglePanel("security")}
        onRequests={() => togglePanel("requests")}
        onInvoices={() => togglePanel("invoices")}
        onWarehouse={() => togglePanel("warehouse")}
        onRouters={() => togglePanel("routers")}
        onSwitches={() => togglePanel("switchmgmt")}
        onWifi={openWifi}
        onDashboard={() => togglePanel("dashboard")}
        onClient360={() => togglePanel("client360")}
        onPitUke={() => togglePanel("pituke")}
        onSidusis={() => togglePanel("sidusis")}
        onAutoPlan={() => togglePanel("autoplan")}
        onStats={() => togglePanel("stats")}
        onSettings={() => { setSettingsTab(null); togglePanel("settings"); }}
        onLicenses={() => { setSettingsTab("licencje"); setActive(null); setPanel("settings"); }}
        isMaster={isMaster}
        onConnections={() => togglePanel("connections")}
        onReservations={() => togglePanel("reservations")}
        onSchedule={() => togglePanel("schedule")}
        onSNMP={() => togglePanel("snmp")}
        onPing={() => togglePanel("ping")}
        onHelp={() => togglePanel("help")}
        onExport={() => setShowExport(true)}
        onSeed={seedDemo} onClearDemo={clearDemo} onLogout={logout} />

      <StatsDrawer data={data} widgets={settings.dashboard_widgets} extra={{ powerStatus }}
        onOpenModule={openModule}
        onOpenSettings={() => { setPanel("settings"); setActive(null); }} />

      {(isManager || user?.role === "infolinia") && <ChatDrawer user={user} />}

      <BuildBadge />

      {/* Top bar: search */}
      {!panelOpen && (
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[750] flex items-center gap-2">
        <SearchBar onPick={pickSearch} />
        <form onSubmit={addressSearch} className="relative">
          <input name="addr" placeholder={t("Adres…")}
            data-testid="address-search-input"
            className="w-40 bg-slate-800/95 backdrop-blur border border-slate-700 rounded-md pl-8 pr-2 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-500" />
          {addressSearching ? <Loader2 size={15} className="absolute left-2.5 top-2.5 text-cyan-400 animate-spin" />
            : <Locate size={15} className="absolute left-2.5 top-2.5 text-slate-400" />}
        </form>
      </div>
      )}

      {/* Map controls top-right */}
      {!panelOpen && (
      <div className="absolute top-3 right-3 z-[750] flex flex-col gap-2 items-end">
        <div className="backdrop-blur-md bg-slate-900/85 border border-slate-700 rounded-md flex overflow-hidden">
          {baseBtns.map((b) => (
            <button key={b.key} onClick={() => setBase(b.key)} title={t(b.title)} data-testid={`base-${b.key}`}
              className={`w-9 h-9 flex items-center justify-center ${base === b.key ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-700"}`}>
              <b.icon size={17} />
            </button>
          ))}
        </div>
        <div className="backdrop-blur-md bg-slate-900/85 border border-slate-700 rounded-md flex overflow-hidden">
          <button onClick={() => { setMeasureMode((m) => !m); setMeasurePts([]); }} title={t("Pomiar odległości")}
            data-testid="measure-toggle"
            className={`w-9 h-9 flex items-center justify-center ${measureMode ? "bg-yellow-600 text-white" : "text-slate-300 hover:bg-slate-700"}`}><Ruler size={17} /></button>
          <button onClick={() => { setBoxMode((m) => !m); setBoxPts([]); setBoxCount(null); }} title={t("Usuń zasięg w zaznaczonym obszarze")}
            data-testid="box-delete-toggle"
            className={`w-9 h-9 flex items-center justify-center ${boxMode ? "bg-red-600 text-white" : "text-slate-300 hover:bg-slate-700"}`}><Trash2 size={17} /></button>
          <button onClick={() => setShowLayers((s) => !s)} title={t("Warstwy mapy")} data-testid="layers-toggle"
            className={`w-9 h-9 flex items-center justify-center ${showLayers ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-700"}`}><Layers size={17} /></button>
          <button onClick={() => setShowImport(true)} title={t("Import GIS")} data-testid="import-open"
            className="w-9 h-9 flex items-center justify-center text-slate-300 hover:bg-slate-700"><Upload size={17} /></button>
          <button onClick={() => setShowClientImport(true)} title={t("Import klientów (CSV/XML)")} data-testid="client-import-open"
            className="w-9 h-9 flex items-center justify-center text-slate-300 hover:bg-slate-700"><Users size={17} /></button>
        </div>

        {showLayers && (
          <div className="backdrop-blur-md bg-slate-900/95 border border-slate-700 rounded-md w-60 max-h-[70vh] overflow-y-auto p-1" data-testid="layers-panel">
            <div className="text-xs text-slate-400 px-2 py-1.5 font-semibold uppercase tracking-wide">{t("Warstwy mapy")}</div>
            <button onClick={() => setShowAddresses((s) => !s)} data-testid="layer-toggle-addresses"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-b border-slate-800 mb-1">
              {showAddresses ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
              <MapPin size={14} style={{ color: showAddresses ? "#38BDF8" : "#475569" }} />
              <span className={showAddresses ? "text-slate-200" : "text-slate-500"}>{t("Baza adresowa")}</span>
              <span className="ml-auto text-xs text-slate-500 font-mono">{addrCount}</span>
            </button>
            {showAddresses && addrCapped && (
              <div className="text-[11px] text-amber-400 px-2 pb-1.5">{t("Pokazano pierwsze 4000 adresów — przybliż mapę, aby zobaczyć pozostałe.")}</div>
            )}
            {showAddresses && (
              <div className="flex items-center gap-3 px-2 pb-1.5 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#22C55E" }} />{t("obsługiwany")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#F97316" }} />{t("potencjał")}</span>
              </div>
            )}
            <button onClick={() => setShowSplitterPower((s) => !s)} data-testid="layer-toggle-power"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-t border-slate-800 mt-1">
              {showSplitterPower ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
              <span style={{ color: showSplitterPower ? "#F59E0B" : "#475569" }}>⚡</span>
              <span className={showSplitterPower ? "text-slate-200" : "text-slate-500"}>{t("Moc i porty splitterów")}</span>
            </button>
            {showSplitterPower && (
              <div className="flex items-center gap-3 px-2 pb-1.5 text-[11px] text-slate-400" data-testid="power-map-legend">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#22C55E" }} />{t("wolne porty")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#F59E0B" }} />{t("mało wolnych")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#DC2626" }} />{t("brak wolnych")}</span>
              </div>
            )}
            <button onClick={() => setRxLayer((s) => { const n = !s; if (n) api.get("/optical/clients-status").then((r) => setPowerStatus(Object.fromEntries(r.data.map((x) => [x.id, x.status])))).catch(() => {}); return n; })} data-testid="layer-toggle-rx"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-t border-slate-800 mt-1">
              {rxLayer ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
              <Zap size={14} style={{ color: rxLayer ? "#F59E0B" : "#475569" }} />
              <span className={rxLayer ? "text-slate-200" : "text-slate-500"}>{t("Jakość sygnału RX")}</span>
            </button>
            {rxLayer && (
              <div className="flex items-center gap-3 px-2 pb-1.5 text-[11px] text-slate-400" data-testid="rx-map-legend">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#22C55E" }} />{t("OK")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#F59E0B" }} />{t("ostrzeżenie")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#DC2626" }} />{t("krytyczny")}</span>
              </div>
            )}
            <button onClick={() => setEgibLayer((s) => !s)} data-testid="layer-toggle-egib"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-t border-slate-800 mt-1">
              {egibLayer ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
              <Milestone size={14} style={{ color: egibLayer ? "#A78BFA" : "#475569" }} />
              <span className={egibLayer ? "text-slate-200" : "text-slate-500"}>{t("Działki (EGiB)")}</span>
            </button>
            {egibLayer && (
              <div className="px-2 pb-1.5 text-[11px] text-slate-500" data-testid="egib-map-legend">{t("Nakładka Geoportal — działki i numery. Widoczna od bliższego zoomu.")}</div>
            )}
            <div className="border-t border-slate-800 mt-1 pt-1">
              <div className="flex items-center gap-2 px-2 py-1 text-sm">
                <Zap size={14} style={{ color: gesutActive ? "#F59E0B" : "#475569" }} />
                <span className={gesutActive ? "text-slate-200" : "text-slate-500"}>{t("Uzbrojenie terenu (GESUT)")}</span>
              </div>
              <div className="flex flex-wrap gap-1 px-2 pb-1.5" data-testid="gesut-media-filters">
                {GESUT_MEDIA.map((mm) => (
                  <button key={mm.key} data-testid={`gesut-media-${mm.key}`} onClick={() => setGesut((s) => ({ ...s, [mm.key]: !s[mm.key] }))}
                    className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors flex items-center gap-1.5 ${gesut[mm.key] ? "border-amber-500 bg-amber-900/30 text-amber-200" : "border-slate-700 text-slate-400 hover:bg-slate-800"}`}>
                    <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ backgroundColor: mm.color }} />
                    {t(mm.label)}
                  </button>
                ))}
              </div>
              {gesutActive && <div className="px-2 pb-1.5 text-[11px] text-slate-500" data-testid="gesut-map-legend">{t("Nakładka Geoportal — wybrane sieci uzbrojenia. Widoczna od bliższego zoomu.")}</div>}
            </div>
            <button onClick={() => setShowWifi((s) => { localStorage.setItem("fp_show_wifi", s ? "0" : "1"); return !s; })} data-testid="layer-toggle-wifi"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-t border-slate-800 mt-1">
              {showWifi ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
              <span className="w-3 h-3 rounded-sm inline-block" style={{ background: showWifi ? "#A855F7" : "#475569" }} />
              <span className={showWifi ? "text-slate-200" : "text-slate-500"}>{t("Sieć WiFi (bazy, sektory, PTP)")}</span>
              <span className="ml-auto text-xs text-slate-500 font-mono">{(wifiData?.sites?.length || 0) + (wifiData?.devices?.length || 0)}</span>
            </button>
            <button onClick={() => setShowSnmp((s) => !s)} data-testid="layer-toggle-snmp"
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-t border-slate-800 mt-1">
              {showSnmp ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
              <Router size={14} style={{ color: showSnmp ? "#22C55E" : "#475569" }} />
              <span className={showSnmp ? "text-slate-200" : "text-slate-500"}>{t("Monitoring SNMP")}</span>
              <span className="ml-auto text-xs text-slate-500 font-mono">{snmpDevices.filter((d) => d.lat != null).length}</span>
            </button>
            {showSnmp && (
              <div className="flex items-center gap-3 px-2 pb-1.5 text-[11px] text-slate-400" data-testid="snmp-map-legend">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#22C55E" }} />{t("online")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#DC2626" }} />{t("offline")}</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#64748B" }} />{t("unknown")}</span>
              </div>
            )}
            {isManager && (
              <button onClick={() => setShowMonters((s) => !s)} data-testid="layer-toggle-monters"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm border-t border-slate-800 mt-1">
                {showMonters ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
                <HardHat size={14} style={{ color: showMonters ? "#EAB308" : "#475569" }} />
                <span className={showMonters ? "text-slate-200" : "text-slate-500"}>{t("Instalatorzy (lokalizacja)")}</span>
                <span className="ml-auto text-xs text-slate-500 font-mono">{monterLocs.length}</span>
              </button>
            )}
            {isManager && showMonters && monterLocs.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] text-slate-500" data-testid="monter-list-empty">{t("Brak instalatorów z aktywną lokalizacją.")}</div>
            )}
            {isManager && showMonters && monterLocs.length > 0 && (
              <div className="pb-1" data-testid="monter-list">
                {monterLocs.map((m) => {
                  const recent = m.ts && (Date.now() - new Date(m.ts).getTime()) < 180000;
                  const active = !!m.active_task || recent;
                  return (
                    <button key={m.user_id} data-testid={`monter-locate-${m.user_id}`}
                      onClick={() => { if (m.lat != null) { setFlyTarget({ lat: m.lat, lng: m.lng }); toast.success(`${t("Pokazuję")}: ${m.name}`); } else { toast.info(t("Brak aktualnej lokalizacji tego instalatora")); } }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-xs text-left">
                      <span className={`w-2 h-2 rounded-full inline-block shrink-0 ${active ? "animate-pulse" : ""}`} style={{ background: m.lat != null ? "#EAB308" : "#475569" }} />
                      <span className="truncate text-slate-200">{m.name}</span>
                      {m.active_task && <span className="ml-auto text-[10px] text-cyan-400 shrink-0 font-mono">#{m.active_task.task_no}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            {isManager && showMonters && monterTrack.length > 1 && (
              <button onClick={() => setMonterTrack([])} data-testid="monter-track-clear"
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-xs text-amber-400">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#EAB308" }} /> {t("Wyczyść ślad trasy")} ({monterTrack.length})
              </button>
            )}
            {MODULE_ORDER.filter((k) => MODULES[k].geo !== "none").map((k) => {              const Icon = MODULES[k].icon;
              const on = visibleModules[k] !== false;
              const count = (data[k] || []).length;
              return (
                <div key={k}>
                  <button onClick={() => toggleModule(k)} data-testid={`layer-toggle-${k}`}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm">
                    {on ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
                    <Icon size={14} style={{ color: on ? MODULES[k].marker : "#475569" }} />
                    <span className={on ? "text-slate-200" : "text-slate-500"}>{t(MODULES[k].label)}</span>
                    <span className="ml-auto text-xs text-slate-500 font-mono">{count}</span>
                  </button>
                  {on && folders.filter((f) => f.module === k).map((f) => (
                    <button key={f.id} onClick={() => toggleFolder(f)} data-testid={`folder-layer-toggle-${f.id}`}
                      className="w-full flex items-center gap-2 pl-7 pr-2 py-1 rounded hover:bg-slate-800 text-xs">
                      {!hiddenFolders.includes(f.id) ? <Eye size={13} className="text-cyan-400" /> : <EyeOff size={13} className="text-slate-600" />}
                      <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ background: f.color || "#94a3b8" }} />
                      <span className={`truncate ${!hiddenFolders.includes(f.id) ? "text-slate-300" : "text-slate-500"}`}>{f.name}</span>
                      <span className="ml-auto text-[10px] text-slate-500 font-mono">{(data[k] || []).filter((it) => it.folder_id === f.id).length}</span>
                    </button>
                  ))}
                  {k === "coverage" && on && (
                    <div className="px-2 pb-2" data-testid="coverage-legend">
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-1.5">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#22C55E" }} />{t("rzeczywisty")}</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#F59E0B" }} />{t("teoretyczny")}</span>
                      </div>
                      <div className="flex gap-1">
                        {[["all", "Wszystkie"], ["rzeczywisty", "Rzeczywiste"], ["teoretyczny", "Teoretyczne"]].map(([v, lbl]) => (
                          <button key={v} onClick={() => setCoverageFilter(v)} data-testid={`coverage-filter-${v}`}
                            className={`flex-1 text-[10px] rounded px-1.5 py-1 border ${coverageFilter === v ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                            {t(lbl)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {(k === "switches" || k === "olt") && on && (
                    <div className="px-2 pb-2" data-testid={`occupancy-legend-${k}`}>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 flex-wrap">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#22C55E" }} />{t("wolne porty")}</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#F59E0B" }} />{t("≤25% wolnych")}</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#DC2626" }} />{t("brak wolnych")}</span>
                      </div>
                    </div>
                  )}
                  {k === "microducts" && on && (
                    <div className="px-2 pb-2 flex items-center gap-3 text-[11px] text-slate-400" data-testid="microduct-legend">
                      <span className="flex items-center gap-1"><span className="w-4 h-0.5 inline-block" style={{ background: "#22C55E" }} />{t("wolne")}</span>
                      <span className="flex items-center gap-1"><span className="w-4 h-0.5 inline-block" style={{ background: "#F59E0B" }} />{t("częściowo")}</span>
                      <span className="flex items-center gap-1"><span className="w-4 h-0.5 inline-block" style={{ background: "#EF4444" }} />{t("pełne")}</span>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="px-2 py-1.5 border-t border-slate-800 mt-1" data-testid="infra-legend">
              <div className="text-[11px] text-slate-400 mb-1.5 font-semibold uppercase tracking-wide">{t("Legenda infrastruktury")}</div>
              <div className="space-y-1 text-[11px] text-slate-300">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3.5 h-3.5 rounded-[3px] border-2 border-slate-900" style={{ background: MODULES.wells.marker }} />
                  {t("Studnia kablowa (właz)")}
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-6 h-0.5" style={{ background: MODULES.ducts.marker }} />
                  {t("Kanalizacja")}
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-6 h-0" style={{ borderTop: `2px dashed ${MODULES.microducts.marker}` }} />
                  {t("Mikrokanalizacja")}
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-6 h-0" style={{ borderTop: `2px dotted ${MODULES.pipelines.marker}` }} />
                  {t("Rurociąg")}
                </div>
              </div>
            </div>
            <div className="px-2 py-1.5 border-t border-slate-800 mt-1">
              <button onClick={() => setShowLinks((v) => !v)} data-testid="layer-toggle-device-links"
                className="w-full flex items-center gap-2 px-0 py-0.5 rounded hover:bg-slate-800 text-sm">
                {showLinks ? <Eye size={15} className="text-pink-400" /> : <EyeOff size={15} className="text-slate-600" />}
                <span className="inline-block w-6 h-0" style={{ borderTop: `2px dashed ${showLinks ? "#EC4899" : "#475569"}` }} />
                <span className={showLinks ? "text-slate-200" : "text-slate-500"}>{t("Patch cordy (ODF↔OLT↔Switch)")}</span>
                <span className="ml-auto text-xs text-slate-500 font-mono">{deviceLinks.length}</span>
              </button>
              {showLinks && deviceLinks.length === 0 && (
                <div className="text-[11px] text-slate-500 pl-7 pt-1">{t("Brak połączeń między różnymi lokalizacjami")}</div>
              )}
            </div>
            {layers.length > 0 && <div className="text-xs text-slate-400 px-2 py-1.5 font-semibold uppercase tracking-wide border-t border-slate-800 mt-1">{t("Warstwy GIS")}</div>}
            {layers.map((l) => {
              const on = l.visible !== false;
              return (
                <button key={l.id} onClick={() => toggleGisLayer(l.id)} data-testid={`gis-layer-toggle-${l.id}`}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 text-sm">
                  {on ? <Eye size={15} className="text-cyan-400" /> : <EyeOff size={15} className="text-slate-600" />}
                  <span className={`truncate ${on ? "text-slate-200" : "text-slate-500"}`}>{l.name}</span>
                  <span className="ml-auto text-xs text-slate-500 font-mono">{l.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Add-on-map banner */}
      {addMode && !pendingCoords && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[760] backdrop-blur-md bg-cyan-900/90 border border-cyan-600 rounded-md px-4 py-2 text-sm flex items-center gap-3">
          {t("Kliknij na mapie, aby ustawić lokalizację")}
          <button onClick={() => setAddMode(null)} className="text-cyan-300 hover:text-white underline">{t("Anuluj")}</button>
        </div>
      )}

      {/* Draw route banner */}
      {snmpPickMode && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[830] backdrop-blur-md bg-green-900/90 border border-green-600 rounded-md px-4 py-2 text-sm flex items-center gap-3" data-testid="snmp-pick-banner">
          <span>{t("Kliknij na mapie, aby ustawić lokalizację urządzenia")}</span>
          <button onClick={() => setSnmpPickMode(false)} className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1">{t("Anuluj")}</button>
        </div>
      )}

      {boxMode && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[760] backdrop-blur-md bg-red-900/90 border border-red-600 rounded-md px-4 py-2 text-sm flex items-center gap-3" data-testid="box-delete-banner">
          <span>{boxPts.length < 2 ? t("Kliknij 2 rogi obszaru na mapie, aby zaznaczyć") : `${t("Obszar zaznaczony")}${boxCount !== null ? ` — ${t("do usunięcia")}: ${boxCount}` : ""}`}</span>
          {boxPts.length === 2 && boxCount === null && (
            <button onClick={boxPreview} disabled={boxBusy} data-testid="box-delete-preview" className="bg-red-600 hover:bg-red-500 rounded px-2.5 py-1 text-white">{t("Sprawdź")}</button>
          )}
          {boxPts.length === 2 && boxCount !== null && (
            <button onClick={boxDelete} disabled={boxBusy || boxCount < 1} data-testid="box-delete-confirm" className="bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded px-2.5 py-1 text-white">{t("Usuń zasięg")}</button>
          )}
          <button onClick={() => { setBoxPts([]); setBoxCount(null); }} data-testid="box-delete-reset" className="text-red-200 hover:text-white underline">{t("Wyczyść")}</button>
          <button onClick={() => { setBoxMode(false); setBoxPts([]); setBoxCount(null); }} data-testid="box-delete-cancel" className="text-red-200 hover:text-white underline">{t("Anuluj")}</button>
        </div>
      )}
      {drawMode && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[760] backdrop-blur-md bg-cyan-900/90 border border-cyan-600 rounded-md px-4 py-2 text-sm flex items-center gap-3" data-testid="draw-banner">
          <span>{t("Rysuj trasę — kliknij punkty")}. {t("Punkty")}: <b className="font-mono">{drawPoints.length}</b></span>
          {drawMode === "extend_cable" && (
            <span className="text-cyan-200 border-l border-cyan-600/50 pl-3" data-testid="extend-length-preview">
              {t("Nowy fragment")}: <b className="font-mono">{Math.round(lineLenM(drawPoints.map((p) => [p.lat, p.lng])))} m</b>
              {" · "}{t("Nowa długość")}: <b className="font-mono text-cyan-100">{Math.round((extendCtx?.baseLength || 0) + lineLenM(drawPoints.map((p) => [p.lat, p.lng])))} m</b>
            </span>
          )}
          <button onClick={() => setSnapOff((v) => !v)} data-testid="draw-snap-toggle"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border ${snapOff ? "bg-amber-500 text-slate-900 border-amber-400" : "bg-slate-700 text-slate-100 border-slate-500 hover:bg-slate-600"}`}
            title={t("Kliknij, aby włączyć/wyłączyć przyciąganie kabla do słupów i muf")}>
            {snapOff ? <Unlink size={13} /> : <Magnet size={13} />}
            {snapOff ? t("Przyciąganie: WYŁ") : t("Przyciąganie: WŁ")}
          </button>
          <button onClick={() => setDrawPoints((p) => p.slice(0, -1))} data-testid="draw-undo" className="text-cyan-200 hover:text-white underline">{t("Cofnij")}</button>
          <button onClick={finishDraw} data-testid="draw-finish" className="bg-blue-600 hover:bg-blue-500 rounded px-2.5 py-1 text-white">{t("Zakończ i zapisz")}</button>
          <button onClick={cancelDraw} data-testid="draw-cancel" className="text-cyan-300 hover:text-white underline">{t("Anuluj")}</button>
        </div>
      )}

      {/* Module list panel */}
      {active && (
        <FloatingPanel id="module" menuVariant={menuVariant} anchor={["documents", "cable_catalog", "closure_catalog", "odf_catalog"].includes(active) ? "right" : "left"} width={460} onClose={() => setActive(null)}>
        <ModulePanel moduleKey={active} items={data[active] || []} slacks={data.cable_slack || []} role={user?.role}
          folders={folders} onFoldersChanged={loadFolders}
          onShowRoute={(it) => { setSpotlight({ module: active, id: it.id, isolate: false, blink: true }); if (it.geometry?.length) setFitBounds(it.geometry.map((p) => [p[0], p[1]])); setTimeout(() => setSpotlight((s) => (s && s.blink && s.id === it.id ? null : s)), 12000); }}
          onEditRoute={(it) => { setActive(null); setSpotlight(null); setEditLine({ module: active, id: it.id, geometry: it.geometry }); if (it.geometry?.length) setFitBounds(it.geometry.map((p) => [p[0], p[1]])); }}
          onClose={() => setActive(null)} onRefresh={loadAll} mapCenter={mapCenter}
          onLocate={(it) => { let lat = it.lat, lng = it.lng; if ((lat == null || lng == null) && it.geometry?.length) { const mid = it.geometry[Math.floor(it.geometry.length / 2)]; if (Array.isArray(mid)) { lat = mid[0]; lng = mid[1]; } } if (lat != null && lng != null) { setFlyTarget({ lat, lng }); setSpotlight({ module: active, id: it.id, isolate: false }); setTimeout(() => setSpotlight((s) => (s && !s.isolate && s.id === it.id ? null : s)), 8000); } }}
          onTrace={doTrace} onFibers={(c) => setFibersCable(c)}
          onDetails={(it) => setDetail({ module: active, item: it })}
          onTrays={(c) => setTrays(c)}
          onSplitter={(s) => setSplitterView(s)}
          onODF={(p) => setOdfView(p)}
          onOLT={(o) => setOltView(o)}
          onSwitch={(s) => setSwitchView(s)}
          onCabinet={(c) => setCabinetView(c)}
          onClosureView={(c) => setClosureView(c)}
          onAddOnMap={() => { setAddMode(active); setPendingCoords(null); }}
          onClientAdded={(saved) => maybePromptClientRoute(saved, true)}
          side={["documents", "cable_catalog", "closure_catalog", "odf_catalog"].includes(active) ? "right" : "left"}
          onDrawOnMap={(noSnap) => { setDrawMode(active); setDrawPoints([]); setSnapOff(!!noSnap); setActive(null); }} />
        </FloatingPanel>
      )}

      {detail && (
        <FloatingPanel id="detail" menuVariant={menuVariant} anchor="right" width={400} onClose={() => { setDetail(null); setSpotlight(null); }}>
        <ObjectDetailPanel moduleKey={detail.module} item={detail.item} slacks={data.cable_slack || []} role={user?.role}
          onClose={() => { setDetail(null); setSpotlight(null); }} onRefresh={loadAll}
          onShowRoute={(it) => {
            setActive(detail.module);
            setSpotlight({ module: detail.module, id: it.id, isolate: false, blink: true });
            if (it.geometry?.length) setFitBounds(it.geometry.map((p) => [p[0], p[1]]));
            setTimeout(() => setSpotlight((s) => (s && s.blink && s.id === it.id ? null : s)), 12000);
          }}
          onEdit={(it) => { setDetail(null); setSpotlight(null); setEditItem({ module: detail.module, item: it }); }}
          onEditRoute={(it) => { setDetail(null); setSpotlight(null); setEditLine({ module: detail.module, id: it.id, geometry: it.geometry }); if (it.geometry?.length) setFitBounds(it.geometry.map((p) => [p[0], p[1]])); }}
          onTrace={(cl) => { setDetail(null); doTrace(cl); }} />
        </FloatingPanel>
      )}

      {editItem && (
        <RecordForm moduleKey={editItem.module} initial={editItem.item} mapCenter={mapCenter}
          onClose={() => setEditItem(null)} onExtendCable={onExtendCable}
          onSaved={() => { setEditItem(null); loadAll(); toast.success(t("Zapisano zmiany")); }} />
      )}

      {/* Record form after map click */}
      {addMode && pendingCoords && (
        <RecordForm moduleKey={addMode} initial={addInitial} presetCoords={pendingCoords} mapCenter={mapCenter}
          drawingActive={clientCableDraw} clientCableGeo={clientCableGeo} onRequestDrawCable={startClientCableDraw}
          onClose={() => { setAddMode(null); setPendingCoords(null); setAddInitial(null); setClientCableGeo(null); }}
          onSaved={(saved) => { const isClient = addMode === "clients"; const wasNew = !addInitial?.id; setAddMode(null); setPendingCoords(null); setAddInitial(null); setClientCableGeo(null); loadAll(); if (isClient) maybePromptClientRoute(saved, wasNew); }} />
      )}

      {/* Record form after drawing a route/cable */}
      {drawGeometry && (
        <RecordForm moduleKey={drawGeometry.module} initial={null} mapCenter={mapCenter}
          presetGeometry={drawGeometry.geometry} presetLength={drawGeometry.length}
          onClose={() => { setDrawGeometry(null); setDrawPoints([]); }}
          onSaved={(saved) => { const hints = drawGeometry.slackHints || []; setDrawGeometry(null); setDrawPoints([]); loadAll(); toast.success(t("Trasa zapisana")); if (saved && saved.id && hints.length) setSlackProposal({ cable: saved, hints }); }} />
      )}

      {fibersCable && <FibersPanel cable={fibersCable} onClose={() => setFibersCable(null)} onTraceFiber={traceFiber} />}
      {clientRoutePrompt && (
        <ClientRoutePrompt client={clientRoutePrompt}
          onYes={() => startClientRouteDraw(clientRoutePrompt)}
          onNo={() => setClientRoutePrompt(null)} />
      )}
      {splitterConnect && (
        <SplitterConnectDialog connect={splitterConnect} clientName={clientRoute?.clientName}
          onDecline={declineSplitter} onConnect={finalizeConnect} />
      )}
      {trays && <TrayView closure={trays} role={user?.role} onFibers={(c) => setFibersCable(c)} onClose={() => setTrays(null)} />}
      {splitterView && <SplitterView splitter={splitterView} role={user?.role} onRefresh={loadAll} onClose={() => setSplitterView(null)}
        onOpenClient={(cid) => { const c = (data.clients || []).find((x) => x.id === cid); if (c) { setSplitterView(null); setDetail({ module: "clients", item: c }); } }} />}
      {odfView && <ODFView panel={odfView} role={user?.role} onRefresh={loadAll} onClose={() => setOdfView(null)}
        onNavigate={(type, id) => {
          if (type === "olt") { const o = (data.olt || []).find((x) => x.id === id); if (o) { setOdfView(null); setOltView(o); } }
          else if (type === "switch") { const s = (data.switches || []).find((x) => x.id === id); if (s) { setOdfView(null); setSwitchView(s); } }
          else if (type === "patchpanel") { const pp = (data.patchpanels || []).find((x) => x.id === id); if (pp) setOdfView(pp); }
        }} />}
      {oltView && <OLTView olt={oltView} role={user?.role} onRefresh={loadAll} onClose={() => setOltView(null)}
        onOpenClient={(cid) => { const c = (data.clients || []).find((x) => x.id === cid); if (c) { setOltView(null); setDetail({ module: "clients", item: c }); } }} />}
      {switchView && <SwitchView sw={switchView} role={user?.role} onRefresh={loadAll} onClose={() => setSwitchView(null)} />}
      {cabinetView && <CabinetView cabinet={cabinetView} role={user?.role} onRefresh={loadAll} onClose={() => setCabinetView(null)} />}
      {trace && (<FloatingPanel id="trace" menuVariant={menuVariant} anchor="right" width={380} onClose={() => { setTrace(null); setTracePath(null); }}><TracePanel trace={trace} onClose={() => { setTrace(null); setTracePath(null); }} onSchematic={() => setSchematic(true)} /></FloatingPanel>)}
      {fiberTrace && (<FloatingPanel id="fiber-trace" menuVariant={menuVariant} anchor="right" width={380} onClose={() => { setFiberTrace(null); setTracePath(null); }}><FiberTracePanel trace={fiberTrace} onClose={() => { setFiberTrace(null); setTracePath(null); }} onStepClick={flyToStep} /></FloatingPanel>)}
      {panel === "otdr" && (<FloatingPanel id="otdr" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><OtdrExplorer onClose={() => setPanel(null)} /></FloatingPanel>)}
      {schematic && trace && <SchematicView trace={trace} onClose={() => setSchematic(false)} />}
      {panel === "reports" && (<FloatingPanel id="reports" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><ReportsPanel onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "connections" && (
        <FloatingPanel id="connections" menuVariant={menuVariant} anchor="right" width={440} onClose={() => { setPanel(null); setTracePath(null); }}>
        <ConnectionsPanel role={user?.role} onClose={() => { setPanel(null); setTracePath(null); }}
          onShowPath={(coords) => setTracePath(coords)} onRefresh={loadAll} />
        </FloatingPanel>
      )}
      {panel === "reservations" && (<FloatingPanel id="reservations" menuVariant={menuVariant} anchor="right" width={560} onClose={() => setPanel(null)}><ReservationsPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "schedule" && (<FloatingPanel id="schedule" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><SchedulePanel role={user?.role} user={user} onClose={() => setPanel(null)}
        onPlanRoute={() => { setPanel(null); setDrawMode("task_cable_route"); setDrawPoints([]); }}
        routeGeo={taskRouteGeo} onRouteConsumed={() => setTaskRouteGeo(null)}
        onLocate={(it) => { if (it.lat != null) setFlyTarget({ lat: it.lat, lng: it.lng }); }} /></FloatingPanel>)}
      {panel === "ping" && (<FloatingPanel id="ping" menuVariant={menuVariant} anchor="right" width={460} onClose={() => setPanel(null)}><PingPanel devices={snmpDevices} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "snmp" && (<FloatingPanel id="snmp" menuVariant={menuVariant} anchor="right" width={640} onClose={() => { setPanel(null); setSnmpInitial(null); setSnmpPickMode(false); }}><SNMPPanel key={snmpInitial?.device || "snmp-panel"} role={user?.role} onClose={() => { setPanel(null); setSnmpInitial(null); setSnmpPickMode(false); }} onChanged={loadSnmp}
        initialTab={snmpInitial?.tab} initialDevice={snmpInitial?.device}
        onRequestPick={() => setSnmpPickMode(true)} pickedCoord={snmpPicked} /></FloatingPanel>)}
      {panel === "faults" && (<FloatingPanel id="faults" menuVariant={menuVariant} anchor="right" width={620} onClose={() => setPanel(null)}><FaultsPanel role={user?.role} data={data} onClose={() => setPanel(null)} onRefresh={loadAll}
        onLocate={(it) => { if (it.lat != null) setFlyTarget({ lat: it.lat, lng: it.lng }); }} /></FloatingPanel>)}
      {panel === "ai" && (<FloatingPanel id="ai" menuVariant={menuVariant} anchor="right" width={400} onClose={() => setPanel(null)}><AIPanel onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "logs" && (<FloatingPanel id="logs" menuVariant={menuVariant} anchor="right" width={380} onClose={() => setPanel(null)}><LogsPanel onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "qrlabels" && (<FloatingPanel id="qrlabels" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><QRLabelsPanel onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "users" && (<FloatingPanel id="users" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><UserManagementPanel currentUser={user} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "security" && <SecurityPanel onClose={() => setPanel(null)} />}
      {panel === "requests" && (<FloatingPanel id="requests" menuVariant={menuVariant} anchor="right" width={640} onClose={() => setPanel(null)}><RequestsPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "invoices" && (<FloatingPanel id="invoices" menuVariant={menuVariant} anchor="right" width={620} onClose={() => setPanel(null)}><InvoicesPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "warehouse" && (<FloatingPanel id="warehouse" menuVariant={menuVariant} anchor="right" width={660} onClose={() => setPanel(null)}><WarehousePanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "routers" && (<FloatingPanel id="routers" menuVariant={menuVariant} anchor="right" width={780} onClose={() => setPanel(null)}><RoutersPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "wifi" && (<FloatingPanel id="wifi" menuVariant={menuVariant} anchor="right" width={900} onClose={() => setPanel(null)}><WifiPanel role={user?.role} nav={wifiNav} mapCenter={mapCenter} onClose={() => setPanel(null)} onChanged={loadWifi} onLocate={(p) => setFlyTarget({ lat: p[0], lng: p[1] })} /></FloatingPanel>)}
      {panel === "switchmgmt" && (<FloatingPanel id="switchmgmt" menuVariant={menuVariant} anchor="right" width={820} onClose={() => setPanel(null)}><SwitchMgmtPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "dashboard" && (<FloatingPanel id="dashboard" menuVariant={menuVariant} anchor="right" width={900} onClose={() => setPanel(null)}><DashboardPanel onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "client360" && (<FloatingPanel id="client360" menuVariant={menuVariant} anchor="right" width={820} onClose={() => { setPanel(null); setClient360Init(null); }}><Client360Panel onClose={() => { setPanel(null); setClient360Init(null); }} initialClientId={client360Init} /></FloatingPanel>)}
      {panel === "pituke" && (<FloatingPanel id="pituke" menuVariant={menuVariant} anchor="right" width={620} onClose={() => setPanel(null)}><PitUkePanel onClose={() => setPanel(null)}
        onLocate={(it) => { if (it.lat != null) setFlyTarget({ lat: it.lat, lng: it.lng }); }} /></FloatingPanel>)}
      {panel === "sidusis" && (<FloatingPanel id="sidusis" menuVariant={menuVariant} anchor="right" width={680} onClose={() => setPanel(null)}><SidusisPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {panel === "autoplan" && (<FloatingPanel id="autoplan" menuVariant={menuVariant} anchor="right" width={560} onClose={() => setPanel(null)}><ErrorBoundary label="Auto-projektant"><AutoPlanPanel role={user?.role} mapCenter={mapCenter} onClose={() => setPanel(null)} /></ErrorBoundary></FloatingPanel>)}
      {panel === "stats" && (<FloatingPanel id="stats" menuVariant={menuVariant} anchor="right" width={560} onClose={() => setPanel(null)}><StatsPanel role={user?.role} onClose={() => setPanel(null)} /></FloatingPanel>)}
      {closureView && <ClosureView closure={closureView} role={user?.role} onClose={() => setClosureView(null)}
        onOpenSplitter={(sid) => { const s = (data.splitters || []).find((x) => x.id === sid); if (s) { setClosureView(null); setSplitterView(s); } }} />}
      {panel === "settings" && (<FloatingPanel id="settings" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><SettingsPanel currentUser={user} initialTab={settingsTab} onClose={() => setPanel(null)}
        onNavigate={(t) => { if (t === "reports" || t === "logs") { setActive(null); setPanel(t); } else { openModule(t); } }} /></FloatingPanel>)}
      {panel === "help" && (<FloatingPanel id="help" menuVariant={menuVariant} width="full" onClose={() => setPanel(null)}><HelpPanel onClose={() => setPanel(null)}
        onStartTour={(id) => { setPanel(null); setActive(null); setTour(TOURS[id]?.steps || null); }}
        onShowMe={(tid) => { setPanel(null); setActive(null); setTimeout(() => { const e = document.querySelector(`[data-testid="${tid}"]`); if (e) { e.scrollIntoView({ behavior: "smooth", block: "center" }); e.classList.add("fp-highlight"); setTimeout(() => e.classList.remove("fp-highlight"), 2800); } }, 320); }} /></FloatingPanel>)}
      {tour && <GuidedTour steps={tour} onClose={() => setTour(null)}
        onAction={(step) => { if (step && step.open) { const [k, key] = step.open.split(":"); if (k === "module") openModule(key); else if (k === "panel") { setActive(null); setPanel(key); } } }} />}
      {showImport && <ImportDialog onClose={() => setShowImport(false)} onImported={loadAll} />}
      {showClientImport && <ClientImportDialog onClose={() => setShowClientImport(false)} onImported={loadAll} />}
      {showExport && <ExportDialog onClose={() => setShowExport(false)} />}
      {slackHost && (
        <CableSlackDialog host={slackHost} allCables={data.cables || []}
          onClose={() => setSlackHost(null)}
          onSaved={() => { setSlackHost(null); loadAll(); }} />
      )}
      {slackProposal && (
        <SlackProposalDialog proposal={slackProposal}
          onClose={() => setSlackProposal(null)}
          onDone={() => { setSlackProposal(null); loadAll(); }} />
      )}
    </div>
  );
}
